import { useCallback, useState } from 'react'
import { message } from 'antd'

import { GQL_CLIENT } from 'src/graphqlClient'
import {
  changeSalesman,
  type ChangeSalesmanTarget,
} from '../services/salesman-assignment.actions'

/**
 * Handing a dossier or a study to another commercial (#769).
 *
 * Shared by the four surfaces that offer it — the architect's project
 * synthesis and study editor, the company's project page and study page — so
 * all four report the same outcome for the same refusal. The server is the only
 * thing that decides: it re-checks who owns the resource and whether the
 * commercial named may receive it, because it writes with the admin client and
 * Hasura's row permissions do not apply there.
 */

export type UseChangeSalesman = {
  /** True while the call is in flight — the caller disables its own control. */
  isSaving: boolean
  /** Resolves to true when the resource actually changed hands. */
  change: (salesmanId: number) => Promise<boolean>
}

/**
 * The server's own sentence, when there is one.
 *
 * Two rejection shapes reach here: urql's `CombinedError` (what `GQL_CLIENT`
 * throws) carries `graphQLErrors`, while a `graphql-request` rejection nests
 * the same array under `response`. Its own `message` is the whole envelope
 * stringified, which is unreadable in a toast — and the refusals worth showing
 * verbatim ("pas un commercial de votre entreprise", "ce commercial est
 * désactivé") are written for the person who pressed the button.
 */
const serverMessage = (err: unknown): string | null => {
  const candidate = err as {
    graphQLErrors?: Array<{ message?: string }>
    response?: { errors?: Array<{ message?: string }> }
  }
  const errors = candidate?.graphQLErrors ?? candidate?.response?.errors
  const first = errors?.[0]?.message?.trim()
  return first && first.length > 0 ? first : null
}

/**
 * Which resource changes hands. Exactly one id is set; the hook is inert while
 * neither is, which is what a page whose data has not loaded yet passes.
 *
 * Taken apart rather than as an object so the callback depends on the two
 * NUMBERS: every caller builds its target inline, and an object literal would
 * be a fresh reference on each render.
 */
export type ChangeSalesmanSubject = {
  projectId?: number | undefined
  urbanAnalysisId?: number | undefined
}

export const useChangeSalesman = (
  { projectId, urbanAnalysisId }: ChangeSalesmanSubject,
  onChanged?: () => void,
): UseChangeSalesman => {
  const [isSaving, setIsSaving] = useState(false)

  const change = useCallback(
    async (salesmanId: number): Promise<boolean> => {
      const target: ChangeSalesmanTarget | null =
        projectId !== undefined
          ? { projectId }
          : urbanAnalysisId !== undefined
            ? { urbanAnalysisId }
            : null
      if (!target) return false
      setIsSaving(true)
      try {
        await changeSalesman(GQL_CLIENT, target, salesmanId)
        message.success('Commercial modifié.')
        onChanged?.()
        return true
      } catch (err) {
        message.error(
          serverMessage(err) ??
            "Impossible de changer le commercial. Réessayez, et contactez votre architecte référent si le problème persiste.",
        )
        return false
      } finally {
        setIsSaving(false)
      }
    },
    [projectId, urbanAnalysisId, onChanged],
  )

  return { isSaving, change }
}
